import React from 'react';
import { TagBadge } from '@chatsdet/ui';
import type { ApiCatalogOccurrenceSummary } from '@chatsdet/types';

/** Section label + optional count chip, matching the drawer's mono-label style. */
export const DrawerSectionLabel: React.FC<{ children: React.ReactNode; count?: number }> = ({
  children,
  count,
}) => (
  <span className="font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground flex items-center gap-2">
    {children}
    {typeof count === 'number' && (
      <span className="font-mono text-[10px] px-1.5 py-px rounded bg-muted text-muted-foreground">
        {count}
      </span>
    )}
  </span>
);

/** Common HTTP status meanings; falls back to the observed label or a generic class. */
const STATUS_MEANINGS: Record<number, string> = {
  200: 'OK',
  201: 'Created',
  202: 'Accepted',
  204: 'No Content',
  301: 'Moved Permanently',
  302: 'Found',
  304: 'Not Modified',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  409: 'Conflict',
  422: 'Unprocessable Entity',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
};

const statusTagColor = (
  code: number
): 'blue' | 'orange' | 'red' | 'gray' => {
  if (code >= 200 && code < 300) return 'blue';
  if (code >= 400 && code < 500) return 'orange';
  if (code >= 500) return 'red';
  return 'gray';
};

/** Light hairline key/value table. Renders an empty state when there are no rows. */
export const KvTable: React.FC<{
  rows: Array<{ key: string; value: React.ReactNode }>;
  emptyText: string;
}> = ({ rows, emptyText }) => {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card px-3.5 py-4 font-mono text-[11.5px] text-muted-foreground">
        {emptyText}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      {rows.map((row) => (
        <div
          key={row.key}
          className="grid grid-cols-[minmax(0,220px)_1fr] gap-3.5 px-3.5 py-2 border-b border-border last:border-b-0 items-start"
        >
          <span className="font-mono text-[12.5px] text-foreground truncate" title={row.key}>
            {row.key}
          </span>
          <span className="font-mono text-[12.5px] text-foreground/80 break-all">{row.value}</span>
        </div>
      ))}
    </div>
  );
};

/** Headers table built from a real occurrence header map. */
export const HeadersTable: React.FC<{
  headers: Record<string, string> | null | undefined;
  emptyText: string;
}> = ({ headers, emptyText }) => {
  const rows = headers
    ? Object.entries(headers).map(([key, value]) => ({ key, value }))
    : [];
  return <KvTable rows={rows} emptyText={emptyText} />;
};

/** Observed status-code distribution computed from recent occurrences. */
export const StatusCodeTable: React.FC<{ occurrences: ApiCatalogOccurrenceSummary[] }> = ({
  occurrences,
}) => {
  const counts = new Map<number, number>();
  for (const occ of occurrences) {
    if (typeof occ.statusCode === 'number') {
      counts.set(occ.statusCode, (counts.get(occ.statusCode) ?? 0) + 1);
    }
  }
  const codes = [...counts.entries()].sort((a, b) => a[0] - b[0]);

  if (codes.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card px-3.5 py-4 font-mono text-[11.5px] text-muted-foreground">
        No status codes observed yet.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[64px_1fr_auto] gap-3.5 px-3.5 h-8 bg-muted/30 border-b border-border items-center font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        <span>Code</span>
        <span>Meaning</span>
        <span>Observed</span>
      </div>
      {codes.map(([code, count]) => (
        <div
          key={code}
          className="grid grid-cols-[64px_1fr_auto] gap-3.5 px-3.5 py-2 border-b border-border last:border-b-0 items-center"
        >
          <TagBadge name={String(code)} color={statusTagColor(code)} hideDot size="sm" />
          <span className="text-[13px] text-foreground/90">
            {STATUS_MEANINGS[code] ?? 'Unknown'}
          </span>
          <span className="font-mono text-[11.5px] text-muted-foreground tabular-nums">
            {count} call{count === 1 ? '' : 's'}
          </span>
        </div>
      ))}
    </div>
  );
};
